import type { v2 } from '@deque/billing-service-client';
import {
  Button,
  FieldGroup,
  FieldWrap,
  PanelContent,
  RadioGroup,
  TextField
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { UserAccessChanges } from '~/app/containers/UserManagement';
import {
  type AccessValue,
  getLicenseDescription,
  getProductOptions
} from '~/app/utils/access-utils';
import { useFeatureFlags } from '~/common/contexts/featureFlags';
import type {
  EnterpriseMembers,
  Enterprises
} from '~/common/utils/billing-client/client-v2';
import styles from './AccessStep.css';

export interface AccessStepProps {
  selectedMembers: EnterpriseMembers.AcceptedMember[];
  licenses: Enterprises.License[];
  products: v2.Product[];
  userAccessChanges: UserAccessChanges;
  setUserAccessChanges: (userAccessChanges: UserAccessChanges) => void;
  handleSubmit: () => void;
}

const AccessStep = ({
  selectedMembers,
  licenses,
  products,
  handleSubmit,
  userAccessChanges,
  setUserAccessChanges
}: AccessStepProps) => {
  const { t } = useTranslation();
  const { featureFlags } = useFeatureFlags();

  const grantGeneralProductAccess = (product: v2.Product) => {
    setUserAccessChanges({
      ...userAccessChanges,
      grantAdminProducts: userAccessChanges.grantAdminProducts.filter(
        p => p.slug !== product.slug
      ),
      grantViewProducts: userAccessChanges.grantViewProducts.filter(
        p => p.slug !== product.slug
      ),
      removeProducts: userAccessChanges.removeProducts.filter(
        p => p.slug !== product.slug
      ),
      grantGeneralProducts: [...userAccessChanges.grantGeneralProducts, product]
    });
  };

  const grantAdminProductAccess = (product: v2.Product) => {
    setUserAccessChanges({
      ...userAccessChanges,
      grantGeneralProducts: userAccessChanges.grantGeneralProducts.filter(
        p => p.slug !== product.slug
      ),
      grantViewProducts: userAccessChanges.grantViewProducts.filter(
        p => p.slug !== product.slug
      ),
      removeProducts: userAccessChanges.removeProducts.filter(
        p => p.slug !== product.slug
      ),
      grantAdminProducts: [...userAccessChanges.grantAdminProducts, product]
    });
  };

  const grantViewProductAccess = (product: v2.Product) => {
    setUserAccessChanges({
      ...userAccessChanges,
      grantGeneralProducts: userAccessChanges.grantGeneralProducts.filter(
        p => p.slug !== product.slug
      ),
      grantAdminProducts: userAccessChanges.grantAdminProducts.filter(
        p => p.slug !== product.slug
      ),
      removeProducts: userAccessChanges.removeProducts.filter(
        p => p.slug !== product.slug
      ),
      grantViewProducts: [...userAccessChanges.grantViewProducts, product]
    });
  };

  const removeProductAccess = (product: v2.Product) => {
    setUserAccessChanges({
      ...userAccessChanges,
      grantGeneralProducts: userAccessChanges.grantGeneralProducts.filter(
        p => p.slug !== product.slug
      ),
      grantAdminProducts: userAccessChanges.grantAdminProducts.filter(
        p => p.slug !== product.slug
      ),
      grantViewProducts: userAccessChanges.grantViewProducts.filter(
        p => p.slug !== product.slug
      ),
      removeProducts: [...userAccessChanges.removeProducts, product]
    });
  };

  const removeAllProductAccess = (product: v2.Product) => {
    setUserAccessChanges({
      ...userAccessChanges,
      grantGeneralProducts: userAccessChanges.grantGeneralProducts.filter(
        p => p.slug !== product.slug
      ),
      grantAdminProducts: userAccessChanges.grantAdminProducts.filter(
        p => p.slug !== product.slug
      ),
      grantViewProducts: userAccessChanges.grantViewProducts.filter(
        p => p.slug !== product.slug
      ),
      removeProducts: userAccessChanges.removeProducts.filter(
        p => p.slug !== product.slug
      )
    });
  };

  const getLicenseRenderData = (license: Enterprises.License) => {
    const product = products.find(p => p.id === license.product_id);

    if (!product) {
      return null;
    }

    const isGeneral = userAccessChanges.grantGeneralProducts.some(
      p => p.slug === product.slug
    );
    const isAdmin = userAccessChanges.grantAdminProducts.some(
      p => p.slug === product.slug
    );
    const isView = userAccessChanges.grantViewProducts.some(
      p => p.slug === product.slug
    );
    const isRemove = userAccessChanges.removeProducts.some(
      p => p.slug === product.slug
    );

    let accessValue: AccessValue = 'none';
    if (isGeneral) {
      accessValue = 'general';
    } else if (isAdmin) {
      accessValue = 'admin';
    } else if (isView) {
      accessValue = 'view';
    } else if (isRemove) {
      accessValue = 'remove';
    }

    const licensesAvailable = license.total
      ? license.total - license.used - license.pending
      : 0;
    const selectedMembersWithLicense = selectedMembers.filter(member =>
      member.product_access.some(pa => pa.product_slug === product.slug)
    ).length;
    const selectedMembersWithoutLicense =
      selectedMembers.length - selectedMembersWithLicense;
    const remainingLicenses = licensesAvailable - selectedMembersWithoutLicense;

    const licenseDescription = getLicenseDescription(
      license,
      selectedMembersWithLicense,
      selectedMembersWithoutLicense,
      remainingLicenses,
      accessValue,
      t
    );
    const productOptions = getProductOptions(
      product,
      selectedMembersWithLicense,
      selectedMembersWithoutLicense,
      remainingLicenses,
      t,
      featureFlags
    );

    return { product, accessValue, licenseDescription, productOptions };
  };

  return (
    <>
      <PanelContent>
        <TextField
          value={selectedMembers
            .map(
              member =>
                `${member.first_name} ${member.last_name} (${member.email})`
            )
            .join('\n')}
          multiline
          readOnly
          label={t('Selected Users ({{userCount}})', {
            userCount: selectedMembers.length
          })}
          className={styles.userList}
        />
        <div className={styles.productList}>
          {licenses.map(license => {
            const data = getLicenseRenderData(license);
            if (!data) {
              return null;
            }
            const { product, accessValue, licenseDescription, productOptions } =
              data;

            return (
              <FieldWrap
                key={product.slug}
                id={`${product.slug}-access`}
                as={FieldGroup}
                label={product.name}
                description={licenseDescription}
              >
                <RadioGroup
                  name={`${product.slug}-access`}
                  aria-label={t('{{productName}} access options', {
                    productName: product.name
                  })}
                  radios={productOptions}
                  value={accessValue}
                  onChange={e => {
                    if (e.value === 'general') {
                      grantGeneralProductAccess(product);
                    } else if (e.value === 'admin') {
                      grantAdminProductAccess(product);
                    } else if (e.value === 'view') {
                      grantViewProductAccess(product);
                    } else if (e.value === 'remove') {
                      removeProductAccess(product);
                    } else if (e.value === 'none') {
                      removeAllProductAccess(product);
                    }
                  }}
                />
              </FieldWrap>
            );
          })}
        </div>
      </PanelContent>
      <PanelContent className={styles.buttonContainer}>
        <Button
          type="submit"
          form="edit-user-form"
          variant="primary"
          disabled={
            userAccessChanges.grantGeneralProducts.length === 0 &&
            userAccessChanges.grantAdminProducts.length === 0 &&
            userAccessChanges.grantViewProducts.length === 0 &&
            userAccessChanges.removeProducts.length === 0
          }
          onClick={handleSubmit}
        >
          {t('Next')}
        </Button>
        <Link to="/user-access" className="Link">
          {t('Cancel')}
        </Link>
      </PanelContent>
    </>
  );
};

export default AccessStep;
